/**
 * ChatListSidebar — the entire left column of the chat page.
 *
 * Extracted from ChatsPage.jsx in Feb 2026 as part of the scalability
 * refactor (ChatsPage.jsx was ~3,200 lines). The sidebar owns filtering,
 * sorting, bulk-actions, search, country/status tabs and the chat list
 * itself. All state still lives in ChatsPage and is passed in as props
 * so the WATI status-as-source-of-truth logic keeps working end-to-end.
 */

import React, { useMemo } from "react";
import {
  Search, Filter, Clipboard, MessageSquarePlus, UserPlus, RefreshCw, Loader2,
  MessageCircle, X,
} from "lucide-react";
import { CategoryBadge, getLabelColor, PRESET_LABELS } from "./ChatRightPanel";
import { getAvatarGradient, getResponseTimer } from "./utils";

const CATEGORIES = [
  "Books", "Sculptures", "Paintings", "Clothing", "Jewelry", "Home and Living",
];
const CATEGORY_COLORS = {
  "Books": { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  "Paintings": { bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-500" },
  "Sculptures": { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  "Clothing": { bg: "bg-teal-50", text: "text-teal-700", dot: "bg-teal-500" },
  "Jewelry": { bg: "bg-pink-50", text: "text-pink-700", dot: "bg-pink-500" },
  "Home and Living": { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
};
const DEFAULT_CAT_COLOR = { bg: "bg-gray-50", text: "text-gray-600", dot: "bg-gray-400" };
const catColor = (cat) => CATEGORY_COLORS[cat] || DEFAULT_CAT_COLOR;

// Iter 132 (Apr 30, 2026) — Status tabs.
// Iter 153 (Feb 6, 2026, Yash) — CRM owns the status enum. PENDING
// dropped (legacy aliased to OPEN on backend). Solved kept; the Solved
// BUTTON in the chat header is what got removed (different control).
const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "open", label: "Open" },
  { key: "solved", label: "Solved" },
  { key: "expired", label: "Expired" },
  { key: "about_to_expire", label: "About to Expire" },
];

/* ---- Chat item with unread badge + category color ---- */
const ChatItem = ({ chat, selected, onClick, formatTime }) => {
  const unread = chat.unread_count || 0;
  const timer = getResponseTimer(chat);
  return (
    <div onClick={onClick}
      className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-all rounded-xl mx-1 mb-0.5 ${selected ? "bg-white border border-[--accent-soft] shadow-sm" : "hover:bg-white/80 hover:shadow-sm"}`}
      data-testid={`chat-item-${chat.id}`}>
      <div className="relative flex-shrink-0">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ background: getAvatarGradient(chat.name), boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}>{chat.name?.charAt(0)?.toUpperCase() || "?"}</div>
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full flex items-center justify-center text-[8px] font-extrabold text-white px-0.5" style={{ background: "linear-gradient(135deg, #EA580C, #E11D48)", boxShadow: "0 1px 4px rgba(234,88,12,0.3)" }} data-testid={`unread-badge-${chat.id}`}>{unread > 9 ? "9+" : unread}</span>}
      </div>
      <div className="flex-1" style={{minWidth: 0, width: 0}}>
        <div className="grid gap-x-2" style={{gridTemplateColumns: "1fr auto"}}>
          <p className={`text-[12px] truncate ${unread > 0 ? "text-[--text-primary] font-bold" : "text-[--text-primary] font-semibold"}`}>{chat.name}</p>
          <div className="flex items-center gap-1">
            {timer && (
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${timer.bg} ${timer.color}`} data-testid={`timer-${chat.id}`}>
                {timer.text}
              </span>
            )}
            <span className={`text-[10px] font-semibold whitespace-nowrap ${unread > 0 ? "text-[--accent]" : "text-[--text-muted]"}`}>{formatTime(chat.last_message_at || chat.updated_at)}</span>
          </div>
          <p className={`text-[11px] truncate ${unread > 0 ? "text-[--text-primary] font-medium" : "text-[--text-muted]"}`}>
            {chat.last_message?.direction === "outgoing" && <span className="text-[--text-muted]">You: </span>}
            {chat.last_message?.content || chat.message || "No messages"}
          </p>
          <div className="flex items-center justify-end">
            {chat.category && <CategoryBadge cat={chat.category} />}
          </div>
        </div>
        {(chat.labels || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {(chat.labels || []).slice(0, 3).map(l => {
              const lc = getLabelColor(l);
              return (
                <span key={l} className="inline-flex items-center gap-0.5 text-[8px] px-1.5 py-[1px] rounded-full font-bold tracking-wide" style={{ background: lc.bg, color: lc.text, border: `1px solid ${lc.border}` }} data-testid={`label-badge-${l}`}>
                  <span className="w-1 h-1 rounded-full" style={{ background: lc.dot }} />
                  {l}
                </span>
              );
            })}
            {(chat.labels || []).length > 3 && <span className="text-[8px] text-[--text-muted]">+{(chat.labels || []).length - 3}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

// Wires up drag-to-scroll and wheel-to-scroll on the status tabs row.
// Extracted as an inline callback ref so the container node is available.
const wireStatusTabsScroll = (el) => {
  if (!el || el.dataset.dragWired) return;
  el.dataset.dragWired = "1";
  let isDown = false, startX = 0, scrollLeft = 0;
  el.addEventListener("mousedown", (e) => {
    isDown = true;
    startX = e.pageX - el.offsetLeft;
    scrollLeft = el.scrollLeft;
  });
  ["mouseleave", "mouseup"].forEach((evt) =>
    el.addEventListener(evt, () => { isDown = false; })
  );
  el.addEventListener("mousemove", (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft = scrollLeft - (x - startX) * 1.5;
  });
  el.addEventListener("wheel", (e) => {
    if (e.deltaY === 0) return;
    e.preventDefault();
    el.scrollLeft += e.deltaY;
  }, { passive: false });
};

export const ChatListSidebar = ({
  // data
  chats, sortedChats, agents, labelOptions, total,
  loading, loadingMore, hasMore,
  selectedChat, user, syncing,
  // filters / sort / search
  sortBy, setSortBy,
  showFilters, setShowFilters, hasActiveFilters,
  filterCategory, setFilterCategory,
  filterAgent, setFilterAgent,
  filterLabel, setFilterLabel,
  filterDepartment, setFilterDepartment,
  clearFilters,
  search, setSearch,
  countryFilter, setCountryFilter,
  statusFilter, setStatusFilter,
  // bulk
  bulkMode, setBulkMode,
  selectedIds, setSelectedIds, setShowBulkMenu,
  selectAllVisible, executeBulkAction, toggleBulkSelect,
  // actions
  setShowNewChatModal, setShowImportModal,
  handleSyncAll, handleChatListScroll, handleSelectChat,
  // refs & utils
  chatListRef, formatTime,
}) => {
  // Iter 156.5 — memoise the bulk-assign target list. agents+user rarely
  // change, but ChatListSidebar re-renders on every chat-list update.
  const bulkAssignableAgents = useMemo(
    () => (agents || []).filter(a => a.is_active !== false && a.id !== user?.id),
    [agents, user?.id]
  );
  return (
    <div
      className={`${selectedChat ? "hidden md:flex" : "flex"} flex-col w-full md:w-[340px] lg:w-[360px] border-r border-[--border] bg-[--bg-cream] flex-shrink-0`}
      data-testid="chat-list-panel"
    >
      <div className="p-4 border-b border-[--border]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-xl font-extrabold text-[--text-primary] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Chats</h2>
            <p className="text-[11px] text-[--text-muted] font-semibold">{total} conversations</p>
          </div>
          <div className="flex items-center gap-1">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-[11px] px-2 py-1 border border-[--border] rounded-full bg-white text-[--text-secondary] hover:bg-gray-50 cursor-pointer"
              data-testid="chat-sort-select"
              title="Sort chats"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="unread">Unread first</option>
              <option value="longest_waiting">Longest waiting</option>
              <option value="alphabetical">A → Z</option>
            </select>
            <button onClick={() => setShowFilters(!showFilters)} className={`btn-icon relative ${showFilters || hasActiveFilters ? "bg-[--accent-pale] text-[--accent]" : ""}`} data-testid="toggle-filters-btn" title="Filters"><Filter size={16} />{hasActiveFilters && <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-[--accent] text-white text-[8px] font-bold rounded-full flex items-center justify-center">{filterCategory.length + filterAgent.length + filterLabel.length + filterDepartment.length}</span>}</button>
            <button onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); setShowBulkMenu(false); }} className={`btn-icon ${bulkMode ? "bg-blue-50 text-blue-600" : ""}`} data-testid="toggle-bulk-btn" title="Bulk Actions"><Clipboard size={16} /></button>
            <button onClick={() => setShowNewChatModal(true)} className="btn-icon" data-testid="new-chat-btn" title="New Chat (WATI template)"><MessageSquarePlus size={16} /></button>
            {user?.role === "admin" || user?.role === "super_admin" && (
              <>
                <button onClick={() => setShowImportModal(true)} className="btn-icon" data-testid="import-contact-btn" title="Import Contact"><UserPlus size={16} /></button>
                <button onClick={handleSyncAll} disabled={syncing} className="btn-icon" data-testid="sync-chats-btn" title="Sync">{syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}</button>
              </>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="mb-2 p-2.5 bg-[--bg-cream] rounded-xl space-y-2 animate-scale-in" data-testid="chat-filters">
            <div className="flex items-center justify-between"><span className="text-[10px] font-semibold text-[--text-muted] uppercase tracking-wider">Filters</span>{hasActiveFilters && <button onClick={clearFilters} className="text-[10px] text-[--accent] hover:underline" data-testid="clear-filters-btn">Clear all</button>}</div>
            {/* Category pills */}
            <div>
              <p className="text-[9px] font-semibold text-[--text-muted] uppercase tracking-wider mb-1">Category</p>
              <div className="flex flex-wrap gap-1" data-testid="filter-category">
                {CATEGORIES.map(c => {
                  const active = filterCategory.includes(c);
                  const cc = catColor(c);
                  return (
                    <button key={c} onClick={() => setFilterCategory(prev => active ? prev.filter(x => x !== c) : [...prev, c])}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border ${active ? `${cc.bg} ${cc.text} border-current shadow-sm` : "bg-white text-[--text-secondary] border-[--border] hover:bg-gray-50"}`}
                      data-testid={`filter-cat-${c}`}>
                      {active && <span className={`inline-block w-1.5 h-1.5 rounded-full ${cc.dot} mr-1`} />}{c}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Agent pills */}
            <div>
              <p className="text-[9px] font-semibold text-[--text-muted] uppercase tracking-wider mb-1">Agent</p>
              <div className="flex flex-wrap gap-1" data-testid="filter-agent">
                {[{ id: "unassigned", name: "Unassigned", _isSpecial: true }, ...agents].map(a => {
                  const active = filterAgent.includes(a.id);
                  const deptShort = a.department === "Customer Support" ? "CS"
                    : a.department === "Sales" ? "Sales"
                    : "";
                  const isLeader = a.role === "leader" || a.role === "admin" || a.role === "super_admin";
                  return (
                    <button key={a.id} onClick={() => setFilterAgent(prev => active ? prev.filter(x => x !== a.id) : [...prev, a.id])}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border inline-flex items-center gap-1 ${active ? "bg-blue-50 text-blue-700 border-blue-300 shadow-sm" : "bg-white text-[--text-secondary] border-[--border] hover:bg-gray-50"}`}
                      data-testid={`filter-agent-${a.id}`}>
                      <span>{a.name}</span>
                      {a._isSpecial ? null : (
                        <>
                          {deptShort && (
                            <span className={`text-[9px] px-1 rounded ${a.department === "Customer Support" ? "bg-pink-50 text-pink-700 border border-pink-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                              {deptShort}
                            </span>
                          )}
                          {isLeader && (
                            <span className="text-[9px] px-1 rounded bg-amber-50 text-amber-700 border border-amber-200">
                              {a.role === "leader" ? "Lead" : "Admin"}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Label pills */}
            <div>
              <p className="text-[9px] font-semibold text-[--text-muted] uppercase tracking-wider mb-1">Label</p>
              <div className="flex flex-wrap gap-1" data-testid="filter-label">
                {labelOptions.map(l => {
                  const active = filterLabel.includes(l);
                  const lc = getLabelColor(l);
                  return (
                    <button key={l} onClick={() => setFilterLabel(prev => active ? prev.filter(x => x !== l) : [...prev, l])}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border ${active ? "shadow-sm" : "bg-white text-[--text-secondary] border-[--border] hover:bg-gray-50"}`}
                      style={active ? { background: lc.bg, color: lc.text, borderColor: lc.border } : {}}
                      data-testid={`filter-label-${l}`}>
                      {active && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: lc.dot }} />}{l}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Department pills */}
            <div>
              <p className="text-[9px] font-semibold text-[--text-muted] uppercase tracking-wider mb-1">Department</p>
              <div className="flex flex-wrap gap-1" data-testid="filter-department">
                {["Sales", "Customer Support"].map(d => {
                  const active = filterDepartment.includes(d);
                  return (
                    <button key={d} onClick={() => setFilterDepartment(prev => active ? prev.filter(x => x !== d) : [...prev, d])}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border ${active ? (d === "Sales" ? "bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm" : "bg-indigo-50 text-indigo-700 border-indigo-300 shadow-sm") : "bg-white text-[--text-secondary] border-[--border] hover:bg-gray-50"}`}
                      data-testid={`filter-dept-${d.toLowerCase().replace(" ", "-")}`}>
                      {active && <span className={`inline-block w-1.5 h-1.5 rounded-full ${d === "Sales" ? "bg-emerald-500" : "bg-indigo-500"} mr-1`} />}{d}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Active filter chips (shown when filter panel is collapsed) */}
        {!showFilters && hasActiveFilters && (
          <div className="mb-2 flex flex-wrap gap-1 items-center" data-testid="active-filter-chips">
            {filterCategory.map(c => {
              const cc = catColor(c);
              return <span key={`cat-${c}`} className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${cc.bg} ${cc.text}`}>
                {c}<button onClick={() => setFilterCategory(prev => prev.filter(x => x !== c))} className="ml-0.5 hover:opacity-60"><X size={9} /></button>
              </span>;
            })}
            {filterAgent.map(a => {
              const agentObj = agents.find(ag => ag.id === a);
              return <span key={`agent-${a}`} className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700">
                {a === "unassigned" ? "Unassigned" : agentObj?.name || a}<button onClick={() => setFilterAgent(prev => prev.filter(x => x !== a))} className="ml-0.5 hover:opacity-60"><X size={9} /></button>
              </span>;
            })}
            {filterLabel.map(l => {
              const lc = getLabelColor(l);
              return <span key={`label-${l}`} className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: lc.bg, color: lc.text }}>
                {l}<button onClick={() => setFilterLabel(prev => prev.filter(x => x !== l))} className="ml-0.5 hover:opacity-60"><X size={9} /></button>
              </span>;
            })}
            {filterDepartment.map(d => (
              <span key={`dept-${d}`} className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${d === "Sales" ? "bg-emerald-50 text-emerald-700" : "bg-indigo-50 text-indigo-700"}`}>
                {d}<button onClick={() => setFilterDepartment(prev => prev.filter(x => x !== d))} className="ml-0.5 hover:opacity-60"><X size={9} /></button>
              </span>
            ))}
            <button onClick={clearFilters} className="text-[9px] text-[--text-muted] hover:text-[--accent] ml-1" data-testid="clear-chips-btn">Clear all</button>
          </div>
        )}

        <div className="relative mb-3">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[--text-muted]" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone..."
            className="w-full bg-white border-2 border-[--border] rounded-full pl-10 pr-4 py-2.5 text-xs font-medium focus:ring-0 focus:border-[--accent-soft] outline-none placeholder:text-[--text-muted] transition-all" data-testid="search-chats-input" />
        </div>

        {/* Country switcher */}
        <div className="flex items-center bg-white rounded-full border-2 border-[--border] p-0.5 mb-2" data-testid="country-filter">
          {[{key: "", label: "All", icon: "🌐"}, {key: "India", label: "India", icon: "🇮🇳"}, {key: "US", label: "US", icon: "🇺🇸"}].map(c => (
            <button key={c.key || "all"} onClick={() => setCountryFilter(c.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold transition-all ${countryFilter === c.key
                ? "bg-[--text-primary] text-white shadow-md"
                : "text-[--text-secondary] hover:bg-gray-50"}`}
              data-testid={`country-filter-${c.key || "all"}`}>
              <span className="text-sm">{c.icon}</span> {c.label}
            </button>
          ))}
        </div>
        <div
          className="flex gap-1.5 overflow-x-auto pb-1 status-filters-row"
          data-testid="status-filters"
          ref={wireStatusTabsScroll}
        >
          {STATUS_TABS.map(t => (
            <button key={t.key} onClick={() => setStatusFilter(t.key)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] font-bold transition-all border-2 ${statusFilter === t.key ? "bg-[--accent] text-white border-[--accent] shadow-sm" : "bg-white text-[--text-secondary] border-[--border-dark] hover:border-[--accent-soft] hover:text-[--accent]"}`}
              data-testid={`status-filter-${t.key || "all"}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {bulkMode && (
        <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2 flex-wrap" data-testid="bulk-action-bar">
          <button onClick={selectAllVisible} className="text-[10px] font-bold text-blue-700 hover:underline" data-testid="select-all-btn">{selectedIds.size === chats.length ? "Deselect All" : "Select All"}</button>
          <span className="text-[10px] text-blue-500 font-bold">{selectedIds.size} selected</span>
          <div className="flex items-center gap-1 ml-auto">
            <button onClick={() => executeBulkAction("assign", user?.id)} className="px-2 py-1 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 hover:bg-blue-200" data-testid="bulk-assign-me">Assign to Me</button>
            {/* Iter 153.43 — bulk-assign to ANY agent (was: only "Assign to Me") */}
            <select
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (v === "__unassign__") {
                  executeBulkAction("assign", "");
                } else {
                  executeBulkAction("assign", v);
                }
                e.target.value = "";
              }}
              className="px-2 py-1 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 cursor-pointer max-w-[140px]"
              data-testid="bulk-assign-to"
              defaultValue=""
            >
              <option value="" disabled>Assign to…</option>
              <option value="__unassign__">— Unassign —</option>
              {bulkAssignableAgents.map(a => (
                <option key={a.id} value={a.id}>{a.name || a.email}</option>
              ))}
            </select>
            <button onClick={() => executeBulkAction("close")} className="px-2 py-1 rounded-full text-[10px] font-bold bg-red-100 text-red-700 hover:bg-red-200" data-testid="bulk-close">Close All</button>
            <button onClick={() => { const l = prompt("Enter label:"); if (l) executeBulkAction("label", l); }} className="px-2 py-1 rounded-full text-[10px] font-bold bg-green-100 text-green-700 hover:bg-green-200" data-testid="bulk-label">Label</button>
            <button onClick={() => { const t = prompt("Enter issue tag:"); if (t) executeBulkAction("tag", t); }} className="px-2 py-1 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 hover:bg-purple-200" data-testid="bulk-tag">Tag</button>
            <button onClick={() => { setBulkMode(false); setSelectedIds(new Set()); }} className="px-2 py-1 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 hover:bg-gray-200" data-testid="bulk-cancel">Cancel</button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto" ref={chatListRef} onScroll={handleChatListScroll} data-testid="chat-scroll-container">
        {loading ? (
          <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
        ) : chats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-[--text-muted]">
            <MessageCircle size={28} className="mb-2 opacity-40" />
            <p className="text-xs">{hasActiveFilters || statusFilter ? "No chats match filters" : "No chats found"}</p>
          </div>
        ) : (
          <>
            {sortedChats.map(chat => (
              <div key={chat.id} className="flex items-center">
                {bulkMode && (
                  <input type="checkbox" checked={selectedIds.has(chat.id)} onChange={() => toggleBulkSelect(chat.id)}
                    className="ml-2 w-4 h-4 rounded border-gray-300 text-blue-600 flex-shrink-0" data-testid={`bulk-check-${chat.id}`} />
                )}
                <div className="flex-1"><ChatItem chat={chat} selected={selectedChat?.id === chat.id} onClick={() => handleSelectChat(chat)} formatTime={formatTime} /></div>
              </div>
            ))}
            {loadingMore && <div className="flex justify-center py-3"><Loader2 size={16} className="animate-spin text-[--text-muted]" /></div>}
            {!hasMore && chats.length > 0 && <p className="text-center text-[10px] text-[--text-muted] py-2">All chats loaded</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatListSidebar;
